import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { MapPin, Settings } from 'lucide-react';
import ProfileCard from '@/components/profile/ProfileCard';
import { EscortProfile } from '@/types';
import { useLanguage } from '@/context/language-context';
import { useAuth } from '@/context/auth';
import { useToast } from '@/hooks/use-toast';

interface ProfileListProps {
  isLoading: boolean;
  profiles: EscortProfile[];
  searchQuery: string;
  favorites: Set<string>;
  onToggleFavorite: (profileId: string) => void;
  onClearSearch: () => void;
  profileImages: Record<string, string>;
  isGeneratingImage: (profileId: string) => boolean;
  onGenerateImage?: (profile: EscortProfile, showToast?: boolean) => Promise<string | void>;
  storageAvailable?: boolean | null;
}

const ProfileList: React.FC<ProfileListProps> = ({
  isLoading,
  profiles,
  searchQuery,
  favorites,
  onToggleFavorite,
  onClearSearch,
  profileImages,
  isGeneratingImage,
  onGenerateImage,
  storageAvailable
}) => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const { toast } = useToast();
  const [bucketsChecked, setBucketsChecked] = useState<boolean | null>(storageAvailable);

  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    // Update our local state when the storage status changes
    if (storageAvailable !== null && bucketsChecked !== storageAvailable) {
      setBucketsChecked(storageAvailable);
    }
  }, [storageAvailable, bucketsChecked]);

  console.log('ProfileList rendering:', { 
    isLoading, 
    profilesLength: profiles.length, 
    profiles: profiles,
    storageAvailable, 
    user: user ? 'logged in' : 'not logged in' 
  });

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 animate-pulse">
        {Array.from({ length: 8 }).map((_, index) => (
          <div key={index} className="bg-card rounded-lg h-[400px]"></div>
        ))}
      </div>
    );
  }
  
  if (!profiles || profiles.length === 0) {
    console.log('No profiles found, showing empty state');
    return (
      <div className="text-center py-12 bg-card rounded-lg shadow-sm">
        <MapPin size={48} className="mx-auto text-muted-foreground mb-4" />
        <h2 className="text-xl font-semibold mb-2">{t('common:search.no_profiles_found')}</h2>
        <p className="text-muted-foreground max-w-md mx-auto">
          {t('common:search.no_profiles_message')}
        </p>
        {searchQuery && (
          <Button 
            variant="link" 
            onClick={onClearSearch}
            className="mt-4"
          >
            {t('search.clear')}
          </Button>
        )}
      </div>
    );
  }
  
  return (
    <div>
      {isAdmin && (
        <div className="mb-6 flex flex-col gap-2">
          <Button 
            variant="outline" 
            onClick={() => window.location.href = '/admin?tab=tools'}
            className="flex items-center gap-2 self-start"
          >
            <Settings size={16} />
            {t('profileManagement.admin_tools')}
          </Button>
        </div>
      )}
      
      {/* Debug des images avant rendu */}
      {process.env.NODE_ENV !== 'production' && (
        <div className="hidden">
          {/* These logs are side-effects, don't render their void return value */}
          {void console.log("DEBUG profileImages complet:", profileImages)}
          {void profiles.forEach(profile => {
            console.log(`DEBUG image pour ${profile.displayName} (${profile.id}):`, profileImages[profile.id]);
          })}
        </div>
      )}
      
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
        {profiles.map((profile) => (
          <ProfileCard 
            key={profile.id} 
            profile={profile} 
            isFavorite={favorites.has(profile.id)}
            onToggleFavorite={onToggleFavorite}
            profileImages={profileImages}
            isGeneratingImage={isGeneratingImage(profile.id)}
            storageAvailable={bucketsChecked}
          />
        ))}
      </div>
    </div>
  );
};

export default ProfileList;
